// components/GhostSignupPopup/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transition: all 0.3s ease-in-out;
`;

const PopupContainer = styled.div`
  background: #08090C;
  border-radius: 16px;
  border:2px solid #592b97;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  transform: ${props => props.isVisible ? 'translateY(0) scale(1)' : 'translateY(-50px) scale(0.95)'};
  transition: all 0.3s ease-in-out;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  
  @media (max-width: 768px) {
    width: 95%;
    margin: 20px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  color: #FFFFFF;
  font-size: 24px;
  cursor: pointer;
  width: 35px;
  height: 35px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s ease;
  z-index: 10;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &:focus {
    outline: 2px solid #3FD2C7;
  }
`;

const FormContainer = styled.div`
  padding: 20px;
  min-height: 360px;
  height: auto;
  
  @media (max-width: 768px) {
    padding: 15px;
    min-height: 320px;
  }
  
  /* Force Ghost iframe to display properly */
  .gh-signup-root {
    min-height: 320px !important;
    height: auto !important;
  }
  
  .gh-signup-root iframe {
    height: 320px !important;
    min-height: 320px !important;
    width: 100% !important;
    max-width: 460px !important;
  }
  
  .gh-signup-root > div {
    position: relative !important;
  }
  
  /* Ensure Ghost form styling doesn't conflict */
  * {
    box-sizing: border-box;
  }
`;

const GhostSignupPopup = ({ 
  showDelay = 3000, // Delay before showing popup (3 seconds)
  showOnlyOnce = true 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const [isClient, setIsClient] = useState(false);
  
  const POPUP_STORAGE_KEY = 'nadiai-ghost-signup-shown';
  const POPUP_EXPIRY_DAYS = 30; // Show again after 30 days

  useEffect(() => {
    setIsClient(true);
  }, []);

  // Check if popup should be shown
  useEffect(() => {
    if (!isClient) return;

    const checkShouldShow = () => {
      if (!showOnlyOnce) {
        setShouldShow(true);
        return;
      }

      try {
        const storedData = localStorage.getItem(POPUP_STORAGE_KEY);
        
        if (!storedData) {
          setShouldShow(true);
          return;
        }

        const { timestamp } = JSON.parse(storedData);
        const now = new Date().getTime();
        const expiryTime = timestamp + (POPUP_EXPIRY_DAYS * 24 * 60 * 60 * 1000);

        if (now > expiryTime) {
          // Expired, show popup again
          localStorage.removeItem(POPUP_STORAGE_KEY);
          setShouldShow(true);
        } else {
          setShouldShow(false);
        }
      } catch (error) {
        console.error('Error checking popup storage:', error);
        setShouldShow(true);
      }
    };

    checkShouldShow();
  }, [isClient, showOnlyOnce]);

  // Show popup with delay
  useEffect(() => {
    if (!shouldShow) return;

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, showDelay);

    return () => clearTimeout(timer);
  }, [shouldShow, showDelay]);

  // Load Ghost signup script when popup becomes visible
  useEffect(() => {
    if (!isVisible) return;

    const loadGhostForm = () => {
      const formContainer = document.getElementById('ghost-signup-container');
      if (!formContainer) return;

      // Clear any existing content
      formContainer.innerHTML = '';

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/ghost/signup-form@~0.2/umd/signup-form.min.js';
      script.setAttribute('data-background-color', '#08090C');
      script.setAttribute('data-text-color', '#FFFFFF');
      script.setAttribute('data-button-color', '#3FD2C7');
      script.setAttribute('data-button-text-color', '#FFFFFF');
      script.setAttribute('data-title', 'NadiAI, Inc');
      script.setAttribute('data-description', 'Simplify the Enterprise with AI Agents - One Platform, Infinite Solutions.');
      script.setAttribute('data-icon', 'https://hub.nadiai.ai/content/images/size/w192h192/size/w256h256/2024/11/nadiAI_128x128-1.png');
      script.setAttribute('data-site', 'https://hub.nadiai.ai/');
      script.setAttribute('data-locale', 'en');
      script.async = true;

      // Add onload handler to fix iframe height
      script.onload = () => {
        console.log('Ghost script loaded');
        
        // Wait a bit for the form to render, then fix the iframe height
        setTimeout(() => {
          const iframes = formContainer.querySelectorAll('iframe');
          iframes.forEach(iframe => {
            console.log('Fixing iframe height');
            iframe.style.height = '320px';
            iframe.style.minHeight = '320px';
            iframe.style.width = '100%';
            iframe.style.maxWidth = '460px';
          });
        }, 1000);

        // Also set up a MutationObserver to catch any dynamic changes
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'childList') {
              const iframes = formContainer.querySelectorAll('iframe');
              iframes.forEach(iframe => {
                if (iframe.style.height === '0px' || !iframe.style.height) {
                  iframe.style.height = '320px';
                  iframe.style.minHeight = '320px';
                }
              });
            }
          });
        });

        observer.observe(formContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style']
        });

        // Cleanup observer when component unmounts
        return () => observer.disconnect();
      };

      script.onerror = () => {
        console.error('Failed to load Ghost signup form');
        formContainer.innerHTML = `
          <div style="color: #FFFFFF; text-align: center; padding: 40px;">
            <h3>Subscribe to NadiAI</h3>
            <p>Sorry, the signup form failed to load. Please visit our website directly.</p>
            <a href="https://hub.nadiai.ai/" target="_blank" style="color: #3FD2C7;">Visit hub.nadiai.ai</a>
          </div>
        `;
      };

      formContainer.appendChild(script);
    };

    // Small delay to ensure the popup is fully rendered
    setTimeout(loadGhostForm, 100);

    return () => {
      const formContainer = document.getElementById('ghost-signup-container');
      if (formContainer) {
        formContainer.innerHTML = '';
      }
    };
  }, [isVisible]);

  const handleClose = () => {
    setIsVisible(false);
    
    if (showOnlyOnce && isClient) {
      try {
        const data = {
          timestamp: new Date().getTime(),
          version: '1.0'
        };
        localStorage.setItem(POPUP_STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.error('Error saving popup state:', error);
      }
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible]);

  if (!shouldShow || !isClient) {
    return null;
  }

  return (
    <PopupOverlay 
      isVisible={isVisible} 
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signup-popup"
    >
      <PopupContainer isVisible={isVisible}>
        <CloseButton 
          onClick={handleClose}
          aria-label="Close popup"
          type="button"
        >
          ×
        </CloseButton>
        <FormContainer>
          <div id="ghost-signup-container" />
        </FormContainer>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default GhostSignupPopup;